import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiCheckCircle, FiUsers, FiTarget, 
  FiEye, FiHeart, FiArrowRight, FiSun 
} from 'react-icons/fi';
import { FaLeaf, FaHandshake, FaMedal, FaGlobeAsia } from 'react-icons/fa';
import { useModal } from '../../context/ModalContext';

const About = () => {
  const { openModal } = useModal();
  const milestones = [
    { year: '2015', title: 'Company Founded', description: 'Started with a vision to provide clean energy solutions to rural India.' },
    { year: '2017', title: 'PM KUSUM Authorization', description: 'Became an authorized EPC provider under PM KUSUM scheme.' },
    { year: '2019', title: '10,000 Installations', description: 'Achieved milestone of 10,000 solar pump installations.' },
    { year: '2021', title: 'Pan-India Expansion', description: 'Expanded operations to 15+ states across India.' },
    { year: '2023', title: '50,000 Installations', description: 'Crossed 50,000 successful solar installations.' },
    { year: '2025', title: 'Industry Leader', description: 'Recognized as one of India\'s top solar EPC providers.' },
  ];

  const values = [
    {
      icon: <FaLeaf className="text-2xl" />,
      title: 'Sustainability',
      description: 'Committed to environmental protection through clean energy solutions.',
    },
    {
      icon: <FiHeart className="text-2xl" />,
      title: 'Customer First',
      description: 'Dedicated to exceeding customer expectations at every step.',
    },
    {
      icon: <FaMedal className="text-2xl" />,
      title: 'Quality Excellence',
      description: 'Uncompromising quality standards in products and services.',
    },
    {
      icon: <FaHandshake className="text-2xl" />,
      title: 'Integrity',
      description: 'Honest and transparent dealings in all business practices.',
    },
  ];

  const team = [
    {
      name: 'Mr. Avin Tapkeer',
      position: 'Director',
      image: '/assets/Director-2.jpeg',
    },
    {
      name: 'Mrs. Arohi Avin Tapkeer',
      position: 'Director',
      image: '/assets/Director-1.jpeg',
    },
  ];

  const certifications = [
    'ISO 9001:2015 Certified',
    'MNRE Approved',
    'PM KUSUM Authorized',
    'BIS Certified Products',
    'IEC Standards Compliant',
    'ALMM Listed Panels',
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[280px] md:h-[340px] flex items-center justify-center overflow-hidden mt-20 md:mt-24">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1920')] bg-cover bg-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-dark-900/90 via-dark-800/80 to-dark-900/70" />
        <div className="container relative z-10 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl md:text-5xl font-extrabold mb-4 uppercase tracking-wide">About Us</h1>
            <p className="text-base md:text-xl text-white/90 mb-4">Powering India's sustainable future with innovative solar solutions</p>
            <div className="flex items-center justify-center gap-2 text-sm text-white/80">
              <Link to="/" className="hover:text-solar-400 transition-colors">Home</Link>
              <span>/</span>
              <span className="text-solar-400">About Us</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-12 md:py-24 bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block text-sm font-bold uppercase tracking-widest text-primary-500 mb-4">Who We Are</span>
              <h2 className="text-2xl md:text-4xl font-bold text-dark-800 mb-4 md:mb-6 leading-tight">India's Leading Solar Energy Solutions Provider</h2>
              <p className="text-dark-500 leading-relaxed mb-4">
                Nath Krupa Energy is a pioneering force in India's renewable energy sector. 
                Established in 2015, we have grown to become one of the nation's most trusted 
                providers of solar energy solutions, serving farmers, homeowners, and businesses alike.
              </p>
              <p className="text-dark-500 leading-relaxed mb-8">
                As an authorized Engineering, Procurement, and Commissioning (EPC) provider under 
                the Pradhan Mantri Kisan Urja Suraksha Evam Utthan Mahabhiyan (PM KUSUM) scheme, 
                we have successfully transformed the lives of thousands of farmers by providing 
                reliable and cost-effective solar-powered irrigation systems.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
                <div className="flex items-center gap-3">
                  <FiSun className="text-3xl text-solar-500" />
                  <div>
                    <span className="block text-2xl font-bold text-dark-800">50,000+</span>
                    <span className="text-sm text-dark-500">Installations</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FiUsers className="text-3xl text-primary-500" />
                  <div>
                    <span className="block text-2xl font-bold text-dark-800">500+</span>
                    <span className="text-sm text-dark-500">Team Members</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FaGlobeAsia className="text-3xl text-primary-500" />
                  <div>
                    <span className="block text-2xl font-bold text-dark-800">15+</span>
                    <span className="text-sm text-dark-500">States</span>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=600" 
                alt="About Nath Krupa Energy"
                className="w-full h-[300px] md:h-[500px] object-cover shadow-2xl rounded-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-12 md:py-24 bg-gray-50">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <motion.div 
              className="bg-white p-10 shadow-lg border-t-4 border-solar-500 rounded-2xl"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-16 h-16 bg-gradient-to-br from-solar-500 to-solar-600 text-white flex items-center justify-center text-3xl mb-6 rounded-xl">
                <FiEye />
              </div>
              <h3 className="text-2xl font-bold text-dark-800 mb-4">Our Vision</h3>
              <p className="text-dark-500 leading-relaxed">
                To be India's most trusted and innovative solar energy company, 
                making clean energy accessible to every home, farm, and business, 
                and contributing to a sustainable future for generations to come.
              </p>
            </motion.div>
            <motion.div 
              className="bg-white p-10 shadow-lg border-t-4 border-secondary-500 rounded-2xl"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="w-16 h-16 bg-gradient-to-br from-secondary-500 to-secondary-600 text-white flex items-center justify-center text-3xl mb-6 rounded-xl">
                <FiTarget />
              </div>
              <h3 className="text-2xl font-bold text-dark-800 mb-4">Our Mission</h3>
              <p className="text-dark-500 leading-relaxed">
                To accelerate India's transition to renewable energy by providing 
                world-class solar solutions that are affordable, reliable, and efficient, 
                while empowering communities and protecting the environment.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-12 md:py-24 bg-white">
        <div className="container">
          <motion.div 
            className="text-center max-w-2xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-bold uppercase tracking-widest text-primary-500 mb-4">Our Core Values</span>
            <h2 className="text-2xl md:text-4xl font-bold text-dark-800 mb-4">Principles That Guide Us</h2>
            <p className="text-dark-500 leading-relaxed">
              Our values define who we are and how we conduct business with our customers, 
              partners, and communities.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {values.map((value, index) => (
              <motion.div 
                key={index} 
                className="text-center p-8 bg-gray-50 hover:bg-white hover:shadow-xl transition-all duration-300 rounded-2xl"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-solar-500 text-white flex items-center justify-center mx-auto mb-5 rounded-xl">
                  {value.icon}
                </div>
                <h4 className="text-lg font-bold text-dark-800 mb-3">{value.title}</h4>
                <p className="text-sm text-dark-500 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey Timeline */}
      <section className="py-12 md:py-24 bg-gray-50">
        <div className="container">
          <motion.div 
            className="text-center max-w-2xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-bold uppercase tracking-widest text-primary-500 mb-4">Our Journey</span>
            <h2 className="text-2xl md:text-4xl font-bold text-dark-800 mb-4">Milestones That Define Us</h2>
            <p className="text-dark-500 leading-relaxed">
              A decade of growth, innovation, and commitment to renewable energy excellence.
            </p>
          </motion.div>
          <div className="relative">
            <div className="absolute left-1/2 -translate-x-1/2 w-1 h-full bg-primary-200 hidden lg:block" />
            <div className="space-y-8 lg:space-y-0">
              {milestones.map((milestone, index) => (
                <motion.div 
                  key={index} 
                  className={`lg:flex items-center gap-8 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className={`lg:w-1/2 ${index % 2 === 0 ? 'lg:text-right lg:pr-12' : 'lg:pl-12'}`}>
                    <div className="bg-white p-6 shadow-lg inline-block rounded-xl">
                      <span className="inline-block bg-gradient-to-r from-primary-500 to-solar-500 text-white px-4 py-1 text-sm font-bold mb-3 rounded-md">{milestone.year}</span>
                      <h4 className="text-lg font-bold text-dark-800 mb-2">{milestone.title}</h4>
                      <p className="text-sm text-dark-500">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="hidden lg:flex w-4 h-4 bg-primary-500 rounded-full absolute left-1/2 -translate-x-1/2" />
                  <div className="lg:w-1/2" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-12 md:py-24 bg-white">
        <div className="container">
          <motion.div 
            className="text-center max-w-2xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-bold uppercase tracking-widest text-primary-500 mb-4">Our Team</span>
            <h2 className="text-2xl md:text-4xl font-bold text-dark-800 mb-4">Leadership Team</h2>
            <p className="text-dark-500 leading-relaxed">
              Meet the experienced professionals driving our mission forward.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 max-w-4xl mx-auto">
            {team.map((member, index) => (
              <motion.div 
                key={index} 
                className="group text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="relative overflow-hidden mb-5 rounded-2xl">
                  <img src={member.image} alt={member.name} className="w-full h-80 object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <h4 className="text-lg font-bold text-dark-800 mb-1">{member.name}</h4>
                <span className="text-sm text-primary-500 font-medium">{member.position}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-12 md:py-24 bg-gray-50">
        <div className="container">
          <motion.div 
            className="text-center max-w-2xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-bold uppercase tracking-widest text-primary-500 mb-4">Certifications</span>
            <h2 className="text-2xl md:text-4xl font-bold text-dark-800 mb-4">Our Credentials</h2>
            <p className="text-dark-500 leading-relaxed">
              Certified and authorized by leading industry bodies and government agencies.
            </p>
          </motion.div>
          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {certifications.map((cert, index) => (
              <div key={index} className="flex items-center gap-4 bg-white p-5 shadow-md rounded-lg">
                <FiCheckCircle className="text-2xl text-secondary-500 flex-shrink-0" />
                <span className="font-semibold text-dark-700">{cert}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-20 bg-gradient-to-r from-secondary-600 to-secondary-700">
        <div className="container">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">Ready to Partner with Us?</h2>
            <p className="text-base md:text-xl text-white/85 mb-6 md:mb-8">Let's work together to build a sustainable energy future for India.</p>
            <button onClick={openModal} className="btn btn-primary">
              Get in Touch <FiArrowRight />
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
